import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // 10 minutes timeout for AI operations (image generation can take time with 10 images)
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

/**
 * Login
 */
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

/**
 * Verify token
 */
export const verifyToken = async () => {
  const response = await api.get('/auth/verify');
  return response.data;
};

/**
 * Fetch content from a URL (preview only, not saved)
 */
export const fetchUrlContent = async (url, rewriteOptions = true) => {
  const payload = { url };
  
  if (rewriteOptions === false) {
    payload.autoRewrite = false;
  } else if (typeof rewriteOptions === 'object') {
    payload.autoRewrite = true;
    payload.rewriteOptions = rewriteOptions;
  } else {
    payload.autoRewrite = true;
  }
  
  const response = await api.post('/content/fetch', payload);
  return response.data;
};

/**
 * Save content to database
 */
export const saveContent = async (url, title, html, imageUrl = null, metadata = {}) => {
  const response = await api.post('/content/save', { 
    url, 
    title, 
    html, 
    imageUrl,
    ...metadata
  });
  return response.data;
};

/**
 * Update existing content
 */
export const updateContent = async (id, title, html, imageUrl = null) => {
  const response = await api.put(`/content/${id}`, { 
    title, 
    html, 
    imageUrl
  });
  return response.data;
};

/**
 * Get all saved content
 */
export const getAllSavedContent = async (page = 1, limit = 20, includeHidden = false) => {
  const params = new URLSearchParams({ page, limit });
  if (includeHidden) {
    params.append('includeHidden', 'true');
  }
  const response = await api.get(`/content?${params.toString()}`);
  return response.data;
};

/**
 * Get single content by ID
 */
export const getContentById = async (id) => {
  const response = await api.get(`/content/${id}`);
  return response.data;
};

/**
 * Delete content by ID
 */
export const deleteContent = async (id) => {
  const response = await api.delete(`/content/${id}`);
  return response.data;
};

/**
 * Toggle hide status of content
 */
export const toggleHideContent = async (id) => {
  const response = await api.patch(`/content/${id}/hide`);
  return response.data;
};

/**
 * Get ALL saved content (no pagination, includes hidden) for admin preview
 */
export const getAllSavedContentUnpaged = async () => {
  const response = await api.get('/content?limit=1000&includeHidden=true');
  return response.data;
};

/**
 * Bulk update section/sortOrder for layout management
 * layout: Array of { id, section, sortOrder }
 */
export const updateLayout = async (layout) => {
  const response = await api.put('/content/layout', { layout });
  return response.data;
};

/**
 * Bulk update categorySortOrder for category page ordering
 */
export const updateCategoryLayout = async (layout) => {
  const response = await api.put('/content/category-layout', { layout });
  return response.data;
};

/**
 * Update the category of a single article immediately (no full content resubmit needed)
 */
export const updateArticleCategory = async (id, category) => {
  const response = await api.patch(`/content/${id}/category`, { category });
  return response.data;
};

/**
 * Bulk import: fetch + rewrite + save a single URL on the backend.
 * Call this in a loop to import multiple URLs one at a time.
 */
export const importUrl = async (url, enableRewrite = true, rewriteOptions = {}) => {
  const response = await api.post('/content/import', { url, enableRewrite, rewriteOptions });
  return response.data;
};

/**
 * Preview article: translate + rewrite manually authored content, handle images.
 * Returns processed data for preview WITHOUT saving to database.
 * Sends multipart/form-data so uploaded image files are included.
 */
export const previewArticle = async (formData) => {
  const response = await api.post('/content/preview-article', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Publish a previously previewed article as featured.
 */
export const publishPreviewedArticle = async (data) => {
  const response = await api.post('/content/publish-article', data);
  return response.data;
};

/**
 * Generate preview-size (small/cheap) images for an article.
 * Supports an optional reference image File (refImage) for DALL-E variation mode.
 * @param {Object} params - { topic, keywords, imageCount, imageSource, imageStyle, previewMode, page, customPrompt, refImage? }
 */
export const generatePreviewImages = async (params) => {
  const { refImage, ...rest } = params;
  if (refImage instanceof File || refImage instanceof Blob) {
    const fd = new FormData();
    Object.entries(rest).forEach(([k, v]) => {
      fd.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v ?? ''));
    });
    fd.append('refImage', refImage);
    const response = await api.post('/content/generate-preview-images', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
  const response = await api.post('/content/generate-preview-images', rest);
  return response.data;
};

/**
 * Build the final article HTML with approved images injected into the body.
 * @param {Object} params - { title, bodyHtml, summary, imagePaths }
 */
export const buildArticleWithImages = async (params) => {
  const response = await api.post('/content/build-article-with-images', params);
  return response.data;
};

export default api;
